

import React, { useEffect, useMemo, useState } from "react";
import useStore from "../../hooks/useStore";
import { STATIC_GROUPS, REVENUE_LABELS } from "../../constants/constants";
import BarChart from "../charts/barChart";
import { observer } from "mobx-react";

function WaterDashboardAccordion({ year, selectedUnit = "KL" }) {
  const {
    dashboardStore: { dashboardDetails },
    companyStore: { companyUnit, revenuePageData },
    userStore,
  } = useStore();

  // Local state for backend group values and loading
  const [groupValues, setGroupValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);

  // Fetch backend group values for water
  useEffect(() => {
    const companyId = Number(
      (userStore.userData && userStore.userData.companyId) ||
        localStorage.getItem("companyId")
    );
    
    if (!companyId || !year) {
      setIsLoading(false);
      return;
    }

    const payload = {
      companyId,
      companyUnit,
      year,
      groupNames: ["D", "E"], // Add more if needed
      reportType: "water",
    };
    
    console.log("Fetching water group values with payload:", payload);
    
    async function fetchGroupValues() {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}company/getGroupValues`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        console.log("Fetched water group values:", data);
        setGroupValues(data.data.water || {});
      } catch (err) {
        console.log("Fetching water group values failed", err);
        console.error("[Error] Fetching group values:", err);
        setGroupValues({});
      } finally {
        setIsLoading(false);
      }
    }
    fetchGroupValues();
  }, [year, companyUnit, userStore.userData]);

  // Conversion logic
  const convertValue = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return value;
    if (fromUnit === "KL" && toUnit === "ML") return value / 1000;
    if (fromUnit === "KL" && toUnit === "M3") return value * 1;
    return value;
  };

  // Prepare water group chart data
  const barChartsGroupData = useMemo(() => {
    if (!dashboardDetails || isLoading) {
      return [];
    }

    const tempData = dashboardDetails?.charts?.water || {};
    const fromUnit = "KL";
    const toUnit = selectedUnit;
    
    const chartData = ["D", "E", "F", "G", "H"].map((group) => {
      const backendGroup = groupValues?.[group];
      
      if (backendGroup) {
        const prevYear = year - 1;
        const chartDataValues = [
          backendGroup[prevYear]?.prevValue ?? 0,
          backendGroup[year]?.prevValue ?? 0,
        ];
        
        const chartUnit =
          backendGroup[year]?.unit ||
          backendGroup[prevYear]?.unit ||
          toUnit;
        
        return {
          label: STATIC_GROUPS.water[group],
          data: chartDataValues,
          unit: chartUnit,
          isValid: chartDataValues && Array.isArray(chartDataValues) && chartDataValues.length > 0,
        };
      }
      
      // Fallback to chart data
      const data = [year - 1, year].map((y) => {
        const temp =
          tempData?.[y]?.reduce((acc, curr) => ({ ...acc, ...curr }), {}) ||
          {};
        const value = temp?.[group]?.prevValue || 0;
        if (group === "D" || group === "E") {
          return value;
        }
        return convertValue(value, fromUnit, toUnit);
      });
      
      let unit = toUnit;
      
      return {
        label: STATIC_GROUPS.water[group],
        data,
        unit,
        isValid: data && Array.isArray(data) && data.length > 0,
      };
    });

    return chartData.filter(item => 
      item && 
      item.label && 
      item.data && 
      Array.isArray(item.data) && 
      item.data.length > 0 &&
      item.unit
    );
  }, [dashboardDetails?.charts?.water, year, selectedUnit, groupValues, isLoading]);

  // Prepare revenue data (like the old version)
  const revenueData = useMemo(() => {
    if (!revenuePageData || !revenuePageData.fieldData || !year || isLoading) {
      return [];
    }

    const filteredData =
      revenuePageData.fieldData.filter(
        (e) => e?.type === "water" && e?.title?.includes("Revenue")
      ) || [];

    const revenueUnit =
      revenuePageData?.revenue?.find((e) => e?.type === "all")?.unit
        ?.shortName || "";

    // Define the groups from REVENUE_LABELS
    const groups = REVENUE_LABELS.water ? Object.keys(REVENUE_LABELS.water) : [];

    if (groups.length === 0) {
      return []; // No water revenue labels defined
    }

    // Create a map of groups with zero values for missing years
    const years = [year - 1, year];
    const dataMap = groups.reduce((acc, group) => {
      acc[group] = years.reduce((yearAcc, y) => {
        yearAcc[y] = 0; // Default to zero for each year
        return yearAcc;
      }, {});
      return acc;
    }, {});

    // Populate dataMap with actual values
    filteredData.forEach((ele) => {
      const title = ele?.title;
      const value = ele?.value || 0;
      const matchingGroup = Object.keys(REVENUE_LABELS.water).find(
        (group) => REVENUE_LABELS.water[group] === title
      );

      if (matchingGroup) {
        years.forEach((y) => {
          if (ele?.year.toString() === y.toString()) {
            dataMap[matchingGroup][y] = value;
          }
        });
      }
    });

    // Transform dataMap into the format required for charts
    const fromUnit = "KL";
    const toUnit = selectedUnit;
    
    return groups.map((group) => {
      const label = REVENUE_LABELS.water[group];
      const isRevenue = label && label.toLowerCase().includes('revenue');
      const data = isRevenue
        ? years.map((y) => dataMap[group][y])
        : years.map((y) => convertValue(dataMap[group][y], fromUnit, toUnit));
      
      return {
        name: label,
        unit: isRevenue ? revenueUnit : "KL",
        data,
        isValid: data && Array.isArray(data) && data.length > 0,
      };
    }).filter(item => item && item.name && item.isValid);
  }, [revenuePageData?.fieldData, revenuePageData?.revenue, year, selectedUnit, isLoading]);

  // Check if data is ready for rendering
  useEffect(() => {
    const hasValidData = barChartsGroupData.length > 0 || revenueData.length > 0;
    setDataReady(hasValidData && !isLoading);
  }, [barChartsGroupData, revenueData, isLoading]);

  // Loading state
  if (isLoading) {
    return (
      <div className="row h-100">
        <div className="col-12 d-flex justify-content-center align-items-center" style={{minHeight: '200px'}}>
          <div>Loading water dashboard data...</div>
        </div>
      </div>
    );
  }

  // No data state
  if (!dataReady || (barChartsGroupData.length === 0 && revenueData.length === 0)) {
    return (
      <div className="row h-100">
        <div className="col-12 d-flex justify-content-center align-items-center" style={{minHeight: '200px'}}>
          <div>No water data available for the selected year.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="row h-100">
      {/* Render bar charts for water groups */}
      {barChartsGroupData.length > 0 &&
        barChartsGroupData.map(({ label, data, unit, isValid }, index) => {
          if (!isValid || !data || data.length === 0) {
            return null;
          }

          return (
            <div key={`water-group-${year}-${label}-${index}`} className="col-xl-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <h4 className="card-title mb-4 donut-heading">{label}</h4>
                    <div className="ms-auto">
                      <div className="toolbar d-flex flex-wrap gap-2">
                        <div>
                          <p className="text-muted mb-1">Unit</p>
                          <h6 className="unit-value">{unit}</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                  <BarChart
                    series={[{ data, name: label }]}
                    categories={[
                      `CY${(year - 1).toString().slice(2)}`,
                      `CY${year.toString().slice(2)}`,
                    ]}
                    unit={unit}
                  />
                </div>
              </div>
            </div>
          );
        })}

      {/* Render bar charts for revenue data */}
      {revenueData.length > 0 &&
        revenueData.map(({ name, data, unit, isValid }, index) => {
          if (!isValid || !data || data.length === 0) {
            return null;
          }

          return (
            <div key={`revenue-chart-${year}-${index}`} className="col-xl-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <h4 className="card-title mb-4 donut-heading">{name}</h4>
                    <div className="ms-auto">
                      <div className="toolbar d-flex flex-wrap gap-2">
                        <div>
                          <p className="text-muted mb-1">Unit</p>
                          <h6 className="unit-value">
                            {dashboardDetails?.charts?.water?.unit} / {unit}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                  <BarChart
                    series={[{ data, name }]}
                    categories={[
                      `CY${(year - 1).toString().slice(2)}`,
                      `CY${year.toString().slice(2)}`,
                    ]}
                    unit={dashboardDetails?.charts?.water?.unit + "/" + unit}
                  />
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}

export default observer(WaterDashboardAccordion);


